import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  AuthResponse,
  Guardian,
  GuardianAuthResponse,
  GuardianLoginInput,
  GuardianRegisterInput,
  ListEducandosResponse,
  ListParticipantBadgesResponse,
  Percurso,
  User,
  UserStatus,
} from 'shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

const GUARDIAN_AUTH = '/api/auth/guardian';

const guardianStatus: Record<Guardian['estado'], UserStatus> = {
  Pendente: 'pending',
  Ativo: 'active',
  Inativo: 'inactive',
};

/** A guardian in the User shape the rest of the PWA reads (name, email). */
export function guardianToUser(g: Guardian): User {
  return {
    id: g.id,
    email: g.email,
    name: `${g.nome} ${g.apelido}`.trim(),
    role: 'user',
    status: guardianStatus[g.estado],
    themePreference: 'light',
    createdAt: g.createdAt,
    updatedAt: g.updatedAt,
  };
}

export const guardianKeys = {
  educandos: (guardianId: number) => ['guardian', guardianId, 'educandos'] as const,
  percurso: (guardianId: number, participantId: number) =>
    ['guardian', guardianId, 'educandos', participantId, 'percurso'] as const,
  badges: (guardianId: number, participantId: number) =>
    ['guardian', guardianId, 'educandos', participantId, 'badges'] as const,
};

/** How often a guardian's list refreshes whether each child is at a centre. */
export const EDUCANDOS_REFETCH_MS = 60_000;

export function useGuardianLogin() {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    mutationFn: async (data: GuardianLoginInput): Promise<AuthResponse> => {
      const response = await api.post<GuardianAuthResponse>(`${GUARDIAN_AUTH}/login`, data, {
        skipAuth: true,
      });
      return {
        user: guardianToUser(response.user),
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
      };
    },
    onSuccess: (response) => setAuth(response, 'guardian'),
  });
}

/** Every guardian registers pending: no session is created here. */
export function useGuardianRegister() {
  return useMutation({
    mutationFn: (data: GuardianRegisterInput) =>
      api.post<{ status: 'pending'; guardianId: number }>(`${GUARDIAN_AUTH}/register`, data, {
        skipAuth: true,
      }),
  });
}

function useGuardianId(): number | undefined {
  const { isAuthenticated, kind, user } = useAuthStore();
  return isAuthenticated && kind === 'guardian' && typeof user?.id === 'number' ? user.id : undefined;
}

/** The participants staff linked to the signed-in guardian. */
export function useEducandos() {
  const guardianId = useGuardianId();
  return useQuery({
    queryKey: guardianKeys.educandos(guardianId ?? 0),
    queryFn: () => api.get<ListEducandosResponse>('/api/guardian/educandos'),
    enabled: guardianId !== undefined,
    refetchInterval: EDUCANDOS_REFETCH_MS,
    staleTime: 30_000,
  });
}

export function useEducandoPercurso(participantId: number) {
  const guardianId = useGuardianId();
  return useQuery({
    queryKey: guardianKeys.percurso(guardianId ?? 0, participantId),
    queryFn: () => api.get<Percurso>(`/api/guardian/educandos/${participantId}/percurso`),
    enabled: guardianId !== undefined && participantId > 0,
    staleTime: 60_000,
  });
}

export function useEducandoBadges(participantId: number) {
  const guardianId = useGuardianId();
  return useQuery({
    queryKey: guardianKeys.badges(guardianId ?? 0, participantId),
    queryFn: () =>
      api.get<ListParticipantBadgesResponse>(`/api/guardian/educandos/${participantId}/badges`),
    enabled: guardianId !== undefined && participantId > 0,
    staleTime: 60_000,
  });
}
